/* ── States ──────────────────────────────────────────────── */

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}

.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: var(--space-6);
  border: 1px dashed var(--border-default);
  border-radius: var(--radius-md);
}

.placeholderText {
  margin: 0;
  font-size: var(--text-base);
  color: var(--text-muted);
  text-align: center;
}

/* ── KPIs ────────────────────────────────────────────────── */

/* Scope caption above the figures. Coverage counts (quarters, regions) live
   here rather than among the stats — they describe what the numbers cover,
   they aren't numbers anyone is reading the page for. */
.kpiScope {
  margin: 0 0 var(--space-4);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.kpiFigure {
  display: flex;
  align-items: baseline;
  gap: var(--space-2);
}

/* Rank against the catalogue — the context a bare figure can't carry. */
.kpiRank {
  padding: 2px var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--bg-active);
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  color: var(--text-secondary);
  white-space: nowrap;
}

.kpiTrend {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: 22px;
  margin-top: var(--space-1);
}

.kpiDelta {
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  white-space: nowrap;
}

.kpiDelta[data-tone='up'] {
  color: var(--brand-green);
}

.kpiDelta[data-tone='down'] {
  color: var(--color-danger);
}

.kpiDelta[data-tone='flat'] {
  color: var(--text-muted);
}

.kpiRow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--space-4);
}

.kpi {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.kpiLabel {
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--text-muted);
}

.kpiValue {
  font-family: var(--font-sans);
  font-size: var(--text-2xl);
  font-weight: var(--weight-bold);
  color: var(--text-primary);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.kpiSub {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/* ── Charts ──────────────────────────────────────────────── */

.chart {
  width: 100%;
}

.donutWrap {
  display: grid;
  grid-template-columns: minmax(0, 220px) minmax(0, 1fr);
  gap: var(--space-5);
  align-items: center;
}

.donutChart {
  min-width: 0;
}

.legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.legendRow {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--text-base);
}

.legendSwatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.legendName {
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.legendValue {
  color: var(--text-primary);
  font-weight: var(--weight-semi);
  font-variant-numeric: tabular-nums;
}

.legendPct {
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  min-width: 48px;
  text-align: right;
}

@media (max-width: 720px) {
  .donutWrap {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* ── Acquisition / retention split ───────────────────────── */

.splitWrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.splitLegend {
  display: flex;
  justify-content: space-between;
  gap: var(--space-4);
  font-size: var(--text-sm);
  font-weight: var(--weight-semi);
}

.splitAcq {
  color: var(--metric-acquired);
}

.splitRet {
  color: var(--metric-retained);
}

/* ── Table ───────────────────────────────────────────────── */

.tableWrap {
  width: 100%;
  overflow-x: auto;
}

.table {
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-family: var(--font-sans);
}

.th {
  padding: 0 var(--space-3) var(--space-2) 0;
  text-align: left;
  font-size: var(--text-xs);
  font-weight: var(--weight-semi);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
  color: var(--text-muted);
  border-bottom: 1px solid var(--border-subtle);
  white-space: nowrap;
}

.tr:hover {
  background: var(--bg-hover);
}

.td {
  height: 48px;
  padding: 0 var(--space-3) 0 0;
  border-bottom: 1px solid var(--border-subtle);
  vertical-align: middle;
  font-size: var(--text-base);
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.tr:last-child .td {
  border-bottom: none;
}

.platformCell {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--text-secondary);
  white-space: nowrap;
}

/* ── Note ────────────────────────────────────────────────── */

/* Every number here is computed from fetched rows — never model-authored. */
.note {
  margin: 0;
  font-size: var(--text-base);
  line-height: var(--leading-relaxed);
  color: var(--text-secondary);
}

.note strong {
  color: var(--text-primary);
  font-weight: var(--weight-semi);
}
